/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import ReactPlayer from 'react-player';
import ForNoImage from 'Assets/Placeholder.png';
import {
  Dialog,
  DialogContent,
  IconButton,
  CircularProgress,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Collapse,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Divider,
} from '@mui/material';
import { endpoints, dataFetcher, axios, baseUrl } from 'Api';
import CreateCategoryPage from 'Pages/private/content-layout/CreateCategory';
import { AppContext } from 'Context';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Pathname } from 'Routes';
import { isNewUIEnabled } from 'hooks/useNewUI';
import { getVideosDetail } from '../../Api/apiFunctions';
import { generateThumbnailsWithWasm } from '../../Utils/wasmThumbnail';
import { generateThumbnailsFromHls, isHlsUrl } from '../../Utils/hlsThumbnail';
import VideoEditModalClassic from './VideoEditModalClassic';

// ---------------------------------------------------------------------------
// V2 theme tokens (match the new dashboard: /your-videos, /viewers, groups)
// ---------------------------------------------------------------------------
const V2 = {
  ink: '#15171c', // primary text + dark CTA
  sub: '#6b7280', // secondary text
  slate: '#475569', // icon accent
  border: 'rgba(15,17,28,0.08)',
  borderStrong: 'rgba(15,17,28,0.14)',
  bg: '#f7f8fa',
  iconBox: '#eef1f5', // slate-tinted icon chip
  field: '#f7f8fa',
};

// Reusable modern "division" container — a soft white card.
const cardBox = {
  backgroundColor: '#fff',
  border: `1px solid ${V2.border}`,
  borderRadius: 16,
  padding: 16,
  boxShadow: '0 1px 3px rgba(15,17,28,0.05)',
};

// Small uppercase micro-label used above each field group.
const fieldLabel = {
  display: 'block',
  marginBottom: 8,
  fontSize: 11.5,
  fontWeight: 700,
  color: V2.sub,
  fontFamily: 'Montserrat',
  textTransform: 'uppercase',
  letterSpacing: '0.6px',
};

// Lucide icons (MIT) inlined so we can animate them on hover without adding a
// dependency. Matches the animated set used across the new dashboard pages.
const lucideProps = (size = 18) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
});
const IconPencil = ({ size, className, ...rest }) => (
  <svg {...lucideProps(size)} className={className} aria-hidden="true" {...rest}>
    <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
    <path d="m15 5 4 4" />
  </svg>
);
const IconDownload = ({ size, className, arrowClassName }) => (
  <svg {...lucideProps(size)} className={className} aria-hidden="true">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <g className={arrowClassName}>
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" x2="12" y1="15" y2="3" />
    </g>
  </svg>
);
const IconX = ({ size, className, ...rest }) => (
  <svg {...lucideProps(size)} className={className} aria-hidden="true" {...rest}>
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);
const IconFolder = ({ size, className, ...rest }) => (
  <svg {...lucideProps(size)} className={className} aria-hidden="true" {...rest}>
    <path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />
  </svg>
);
const IconGlobe = ({ size, className, ...rest }) => (
  <svg {...lucideProps(size)} className={className} aria-hidden="true" {...rest}>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
    <path d="M2 12h20" />
  </svg>
);
const IconLock = ({ size, className, ...rest }) => (
  <svg {...lucideProps(size)} className={className} aria-hidden="true" {...rest}>
    <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);
const IconUsers = ({ size, className, ...rest }) => (
  <svg {...lucideProps(size)} className={className} aria-hidden="true" {...rest}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);
const IconPlus = ({ size, className, ...rest }) => (
  <svg {...lucideProps(size)} className={className} aria-hidden="true" {...rest}>
    <path d="M5 12h14" />
    <path d="M12 5v14" />
  </svg>
);
const IconListMusic = ({ size, className, ...rest }) => (
  <svg {...lucideProps(size)} className={className} aria-hidden="true" {...rest}>
    <path d="M21 15V6" />
    <path d="M18.5 18a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" />
    <path d="M12 12H3" />
    <path d="M16 6H3" />
    <path d="M12 18H3" />
  </svg>
);
const IconChevronDown = ({ size, className, ...rest }) => (
  <svg {...lucideProps(size)} className={className} aria-hidden="true" {...rest}>
    <path d="m6 9 6 6 6-6" />
  </svg>
);
const IconChevronUp = ({ size, className, ...rest }) => (
  <svg {...lucideProps(size)} className={className} aria-hidden="true" {...rest}>
    <path d="m18 15-6-6-6 6" />
  </svg>
);
const IconCheck = ({ size, className, ...rest }) => (
  <svg {...lucideProps(size)} className={className} aria-hidden="true" {...rest}>
    <path d="M20 6 9 17l-5-5" />
  </svg>
);
const IconUpload = ({ size, className, ...rest }) => (
  <svg {...lucideProps(size)} className={className} aria-hidden="true" {...rest}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" x2="12" y1="3" y2="15" />
  </svg>
);
const IconPlay = ({ size, className, ...rest }) => (
  <svg {...lucideProps(size)} className={className} aria-hidden="true" fill="currentColor" stroke="none" {...rest}>
    <polygon points="6 3 20 12 6 21 6 3" />
  </svg>
);
const IconFilm = ({ size, className, ...rest }) => (
  <svg {...lucideProps(size)} className={className} aria-hidden="true" {...rest}>
    <rect width="18" height="18" x="3" y="3" rx="2" />
    <path d="M7 3v18" />
    <path d="M3 7.5h4" />
    <path d="M3 12h18" />
    <path d="M3 16.5h4" />
    <path d="M17 3v18" />
    <path d="M17 7.5h4" />
    <path d="M17 16.5h4" />
  </svg>
);
const IconSliders = ({ size, className, ...rest }) => (
  <svg {...lucideProps(size)} className={className} aria-hidden="true" {...rest}>
    <line x1="4" x2="4" y1="21" y2="14" />
    <line x1="4" x2="4" y1="10" y2="3" />
    <line x1="12" x2="12" y1="21" y2="12" />
    <line x1="12" x2="12" y1="8" y2="3" />
    <line x1="20" x2="20" y1="21" y2="16" />
    <line x1="20" x2="20" y1="12" y2="3" />
    <line x1="2" x2="6" y1="14" y2="14" />
    <line x1="10" x2="14" y1="8" y2="8" />
    <line x1="18" x2="22" y1="16" y2="16" />
  </svg>
);
const IconImage = ({ size, className, ...rest }) => (
  <svg {...lucideProps(size)} className={className} aria-hidden="true" {...rest}>
    <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
    <circle cx="9" cy="9" r="2" />
    <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
  </svg>
);

// Scoped hover/animation styles for the modal's Lucide icons — each icon plays
// its own signature motion on hover, in the spirit of lucide-animated.com.
const SPRING = 'cubic-bezier(0.34,1.56,0.64,1)';
const VEM_STYLES = `
  /* buttons lift on hover */
  .vem-iconbtn { transition: transform .18s ${SPRING}, background-color .18s ease, box-shadow .18s ease; }
  .vem-iconbtn:hover:not(:disabled) { transform: translateY(-1px); }
  .vem-iconbtn:active:not(:disabled) { transform: translateY(0); }

  /* download — arrow bounces down */
  .vem-dl-arrow { transition: transform .25s ${SPRING}; }
  .vem-iconbtn:hover:not(:disabled) .vem-dl-arrow { animation: vemBounce .6s ease infinite; }
  @keyframes vemBounce { 0%,100% { transform: translateY(0); } 50% { transform: translateY(2.5px); } }

  /* header pencil — writes with a springy wiggle */
  .vem-pencil { transition: transform .3s ${SPRING}; transform-origin: bottom left; }
  .vem-chip:hover .vem-pencil { animation: vemWrite .6s ease; }
  @keyframes vemWrite { 0% { transform: rotate(0) translateX(0); } 30% { transform: rotate(-14deg) translateX(-1px); } 60% { transform: rotate(7deg) translateX(1px); } 100% { transform: rotate(0); } }

  /* close — rotates a quarter turn */
  .vem-x { transition: transform .25s ${SPRING}; }
  .vem-iconbtn:hover:not(:disabled) .vem-x { transform: rotate(90deg); }

  /* additional options — sliders pop, chevron bobs */
  .vem-sliders, .vem-chev { transition: transform .25s ${SPRING}; }
  .vem-opt-toggle:hover .vem-sliders { transform: scale(1.14); }
  .vem-opt-toggle:hover .vem-chev { transform: translateY(2px); }

  /* save — check draws itself in */
  .vem-save-check path { stroke-dasharray: 24; stroke-dashoffset: 0; }
  .vem-iconbtn:hover:not(:disabled) .vem-save-check path { animation: vemDraw .5s ease; }
  @keyframes vemDraw { from { stroke-dashoffset: 24; } to { stroke-dashoffset: 0; } }

  /* play — pops */
  .vem-play { transition: transform .2s ${SPRING}; }
  .vem-iconbtn:hover .vem-play { transform: scale(1.18); }

  /* custom thumbnail — upload arrow lifts */
  .vem-upload { transition: transform .25s ${SPRING}; }
  .vem-upload-tile:hover .vem-upload { transform: translateY(-2px); }

  /* dropdown fields / inputs — rounded, hairline border, ink focus ring */
  .vem-root .MuiOutlinedInput-root { border-radius: 12px; font-family: Montserrat; transition: box-shadow .18s ease; }
  .vem-root .MuiOutlinedInput-notchedOutline { border-color: rgba(15,17,28,0.10); transition: border-color .15s ease; }
  .vem-root .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline { border-color: rgba(15,17,28,0.20); }
  .vem-root .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline { border-color: #15171c; border-width: 1.5px; }
  .vem-root .MuiOutlinedInput-root.Mui-focused { box-shadow: 0 0 0 3px rgba(15,17,28,0.08); }
  .vem-root .MuiSelect-icon { color: #6b7280; }

  /* dropdown sheet (menu popover) — rounded card, soft shadow, padded list */
  .vem-menu { border-radius: 14px !important; box-shadow: 0 14px 40px rgba(15,17,28,0.16), 0 2px 8px rgba(15,17,28,0.06) !important; border: 1px solid rgba(15,17,28,0.06); margin-top: 8px; }
  .vem-menu .MuiList-root { padding: 6px; }
  .vem-menu .MuiMenuItem-root { border-radius: 10px; margin: 2px 4px; padding: 8px 10px; font-family: Montserrat; font-size: 13px; transition: background-color .15s ease; }
  .vem-menu .MuiMenuItem-root:hover { background-color: #f2f4f7; }
  .vem-menu .MuiMenuItem-root.Mui-selected { background-color: #eef1f5; }
  .vem-menu .MuiMenuItem-root.Mui-selected:hover { background-color: #e6eaf0; }
  .vem-menu .MuiDivider-root { margin: 5px 8px; background-color: rgba(15,17,28,0.07); }
`;

const VideoEditModalNew = ({
  open,
  onClose,
  videoId = null,
  onSaveSuccess = null,
  onDownloadVideos = null,
  initialVideoData = null,
}) => {
  const {
    userDetails,
    setAppSnackbar,
    autoSignIn,
    defaultCategoryName,
    setShowAddPlaylistModal,
    playlistRefreshTrigger,
    newlyCreatedPlaylistId,
    setNewlyCreatedPlaylistId,
    setShowAddSubCategoryModal,
    subCategoryRefreshTrigger,
    newlyCreatedSubCategoryId,
    setNewlyCreatedSubCategoryId,
    downloadingFiles,
  } = useContext(AppContext);

  const userId = userDetails?.isSubscription?.user_id;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isFromGift = searchParams.get('fromGift') === 'true';

  // Single video states
  const [videoData, setVideoData] = useState(null);
  console.log('videoData', videoData);

  const [isLoading, setIsLoading] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState('2');
  const [selectedCategory, setSelectedCategory] = useState({
    value: '',
    name: '',
  });
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [selectedPlaylists, setSelectedPlaylists] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [thumbnails, setThumbnails] = useState([]);
  const [selectedThumbnail, setSelectedThumbnail] = useState(0);
  const [customThumbnail, setCustomThumbnail] = useState(null);
  const [isCustomThumbnailSelected, setIsCustomThumbnailSelected] = useState(false);
  const [isGeneratingThumbs, setIsGeneratingThumbs] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const customThumbnailInputRef = useRef(null);
  const [showAdditionalFields, setShowAdditionalFields] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [videoDuration, setVideoDuration] = useState(0);
  const [videoSize, setVideoSize] = useState(0);
  const [isDownloadDisabled, setIsDownloadDisabled] = useState(true);
  const isAlreadyDownloading = downloadingFiles?.some(
    (f) => ((f.videoId || f.id) === videoData?.content_id || (f.videoId || f.id) === videoId)
      && (f.status === 'downloading' || f.status === 'pending')
  );
  const additionalOptionsRef = useRef(null);

  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [groups, setGroups] = useState([]);
  const [groupsLoaded, setGroupsLoaded] = useState(false);
  const [showCreateCategoryModal, setShowCreateCategoryModal] = useState(false);

  // Video URL fallback logic (same as video-player-0)
  const [currentVideoUrl, setCurrentVideoUrl] = useState(null);
  const [hasTriedFallback, setHasTriedFallback] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const loadTimeoutRef = useRef(null);
  // Guards against a duplicate details fetch when `videoId` prop changes value
  // for the same open session (e.g. parent resolves the id on a later render).
  const fetchedVideoIdRef = useRef(null);

  // Timeout-based fallback: if video doesn't play within timeout, switch to video_raw_file
  useEffect(() => {
    if (currentVideoUrl && !hasTriedFallback) {
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
      }
      const isHlsStream = currentVideoUrl?.includes('.m3u8');
      const timeoutDuration = isHlsStream ? 10000 : 6000;
      loadTimeoutRef.current = setTimeout(() => {
        if (!hasTriedFallback && videoData?.video_raw_file) {
          setCurrentVideoUrl(videoData.video_raw_file);
          setHasTriedFallback(true);
        } else if (!hasTriedFallback) {
          setVideoFailed(true);
        }
      }, timeoutDuration);
    }
    return () => {
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
      }
    };
  }, [currentVideoUrl, hasTriedFallback, videoData]);

  // Handle video error - fallback to video_raw_file
  const handleVideoError = useCallback((error) => {
    if (error?.name === 'NotAllowedError' || (error?.message && error.message.includes('interact'))) {
      return;
    }
    if (loadTimeoutRef.current) {
      clearTimeout(loadTimeoutRef.current);
      loadTimeoutRef.current = null;
    }
    if (!hasTriedFallback && videoData?.video_raw_file) {
      setCurrentVideoUrl(videoData.video_raw_file);
      setHasTriedFallback(true);
    } else {
      setVideoFailed(true);
    }
  }, [hasTriedFallback, videoData]);

  // Clear video URL state on video ready (cancel timeout)
  const handleVideoReady = useCallback(() => {
    if (loadTimeoutRef.current) {
      clearTimeout(loadTimeoutRef.current);
      loadTimeoutRef.current = null;
    }
  }, []);

  // Fetch video details when modal opens
  useEffect(() => {
    // Reset the guard when the modal closes so reopening refetches.
    if (!open) {
      fetchedVideoIdRef.current = null;
      return undefined;
    }
    const fetchVideoDetails = async () => {
      if (!videoId) return;
      // Skip if we've already fetched (or are fetching) this same id this session.
      if (fetchedVideoIdRef.current === videoId) return;
      fetchedVideoIdRef.current = videoId;

      setIsLoading(true);
      setIsDownloadDisabled(true);
      try {
        // Reuse details already fetched by the parent (e.g. YourVideos prefetches
        // getVideosDetail before opening) to avoid a duplicate API call.
        let videoDetails = null;
        if (initialVideoData && String(initialVideoData.id) === String(videoId)) {
          videoDetails = initialVideoData;
        } else {
          const response = await getVideosDetail({ id: videoId });
          if (response?.data?.success && response?.data?.data) {
            videoDetails = response.data.data;
          }
        }
        if (videoDetails) {
          setVideoData(videoDetails);

          // Set video preview URL with priority: transcoded_url > video_file > mp4_url > video_raw_file
          const primaryUrl = videoDetails?.transcoded_url || videoDetails?.video_file || videoDetails?.mp4_url || videoDetails?.video_raw_file;
          setCurrentVideoUrl(primaryUrl);
          setHasTriedFallback(false);
          setVideoFailed(false);

          // Check download availability - verify the file actually exists on S3
          const rawFileUrl = videoDetails?.video_raw_file;
          if (rawFileUrl && rawFileUrl.trim() !== '') {
            try {
              // Range request with just 1 byte (fastest way to check file exists)
              // fileCheckResponse = await fetch(rawFileUrl, { method: 'HEAD' });
              const fileCheckResponse = await fetch(rawFileUrl, {
                method: 'GET',
                headers: { Range: 'bytes=0-0' },
              });

              // Check content-type - S3 errors return application/xml
              const contentType = fileCheckResponse.headers.get('content-type') || '';
              const isXmlError = contentType.includes('xml');

              setIsDownloadDisabled(!fileCheckResponse.ok || isXmlError);
            } catch {
              setIsDownloadDisabled(true);
            }
          } else {
            setIsDownloadDisabled(true);
          }

          // Populate form fields
          setTitle(isFromGift ? '' : (videoDetails.title || ''));
          setDescription(videoDetails.description || '');
          setVisibility(videoDetails.visibility || '2');
          setVideoDuration(videoDetails.video_duration || 0);
          setVideoSize(videoDetails.video_size || 0);

          // Set category
          if (videoDetails.category_id) {
            setSelectedCategory({
              value: videoDetails.category_id,
              name: videoDetails.category_title || '',
            });
          }

          // Set subcategory
          setSelectedSubCategory(videoDetails.subcategory_id || '');

          // Set playlists - handle both string and array formats, convert to numbers
          if (videoDetails.playlists) {
            try {
              const playlistIds = typeof videoDetails.playlists === 'string'
                ? JSON.parse(videoDetails.playlists)
                : videoDetails.playlists;
              const rawIds = Array.isArray(playlistIds) ? playlistIds : [playlistIds];
              setSelectedPlaylists(rawIds.map((id) => Number(id)).filter((id) => !Number.isNaN(id)));
            } catch (e) {
              // If it's a single value, wrap in array
              const id = Number(videoDetails.playlists);
              setSelectedPlaylists(Number.isNaN(id) ? [] : [id]);
            }
          } else if (videoDetails.playlist_ids) {
            try {
              let playlistIds;
              if (typeof videoDetails.playlist_ids === 'string') {
                if (videoDetails.playlist_ids.trim().startsWith('[')) {
                  playlistIds = JSON.parse(videoDetails.playlist_ids);
                } else {
                  playlistIds = videoDetails.playlist_ids.split(',').map((id) => Number(id.trim()));
                }
              } else {
                playlistIds = videoDetails.playlist_ids;
              }
              const rawIds = Array.isArray(playlistIds) ? playlistIds : [playlistIds];
              setSelectedPlaylists(rawIds.map((id) => Number(id)).filter((id) => !Number.isNaN(id)));
            } catch (e) {
              setSelectedPlaylists([]);
            }
          } else if (videoDetails.playlist_id) {
            const id = Number(videoDetails.playlist_id);
            setSelectedPlaylists(Number.isNaN(id) ? [] : [id]);
          }

          // Set groups - handle both string and array formats
          if (videoDetails.group_id) {
            try {
              const groupIds = typeof videoDetails.group_id === 'string'
                ? JSON.parse(videoDetails.group_id)
                : videoDetails.group_id;
              setSelectedGroups(Array.isArray(groupIds) ? groupIds : []);
            } catch (e) {
              setSelectedGroups([]);
            }
          }

          // Set thumbnails from API response
          if (videoDetails.thumbnail_list) {
            try {
              // Parse thumbnail_list if it's a JSON string
              const thumbnailArray = typeof videoDetails.thumbnail_list === 'string'
                ? JSON.parse(videoDetails.thumbnail_list)
                : videoDetails.thumbnail_list;

              if (Array.isArray(thumbnailArray) && thumbnailArray.length > 0) {
                setThumbnails(thumbnailArray);
                const currentThumbIndex = thumbnailArray.indexOf(videoDetails?.thumbnails[0]);
                console.log('thumbnailArray', thumbnailArray, currentThumbIndex);
                setSelectedThumbnail(currentThumbIndex !== -1 ? currentThumbIndex : 0);
              } else if (videoDetails.thumbnail) {
                setThumbnails([videoDetails.thumbnail]);
                setSelectedThumbnail(0);
              }
            } catch (e) {
              console.error('Error parsing thumbnail_list:', e);
              // Fallback to single thumbnail if parsing fails
              if (videoDetails.thumbnail) {
                setThumbnails([videoDetails.thumbnail]);
                setSelectedThumbnail(0);
              }
            }
          } else if (videoDetails.thumbnail) {
            setThumbnails([videoDetails.thumbnail]);
            setSelectedThumbnail(0);
          }
        } else {
          setAppSnackbar({
            open: true,
            message: 'Failed to load video details',
            severity: 'error',
          });
        }
      } catch (error) {
        console.error('Error fetching video details:', error);
        setAppSnackbar({
          open: true,
          message: 'Error loading video details',
          severity: 'error',
        });
        setIsDownloadDisabled(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchVideoDetails();
  }, [open, videoId]);

  // Fetch categories, subcategories, playlists, groups
  useEffect(() => {
    const fetchAllData = async () => {
      if (!open) return;

      // Reset loaded states
      setGroupsLoaded(false);

      try {
        // Fetch categories
        const categoriesResponse = await dataFetcher(endpoints.getMyCategories);
        if (categoriesResponse.success && categoriesResponse.data) {
          // Sort categories to always show "All Videos" at the top
          const sortedCategories = [...categoriesResponse.data].sort((a, b) => {
            const aIsAllVideos = a.title?.toLowerCase() === 'all videos';
            const bIsAllVideos = b.title?.toLowerCase() === 'all videos';
            if (aIsAllVideos && !bIsAllVideos) return -1;
            if (!aIsAllVideos && bIsAllVideos) return 1;
            return 0; // Keep original order for other categories
          });
          setCategories(sortedCategories);
        }

        // Fetch subcategories
        const subCategoriesResponse = await dataFetcher(endpoints.getMySubCategory);
        if (subCategoriesResponse.success && subCategoriesResponse.data) {
          setSubCategories(subCategoriesResponse.data);
        }

        // Fetch playlists
        const playlistsResponse = await dataFetcher(endpoints.getMyPlaylist);
        if (playlistsResponse.success && playlistsResponse.data) {
          setPlaylists(playlistsResponse.data);
        }

        // Fetch groups
        const groupsResponse = await dataFetcher(endpoints.getMyGroups);
        if (groupsResponse.success && groupsResponse.data) {
          setGroups(groupsResponse.data);
        } else if (!groupsResponse.success && groupsResponse.message === 'No Groups found') {
          setGroups([]);
        }
        setGroupsLoaded(true);
      } catch (error) {
        console.error('Error fetching data:', error);
        setGroups([]);
        setGroupsLoaded(true);
      }
    };

    fetchAllData();
  }, [open]);

  // Refresh playlists when a new playlist is created from the global modal
  useEffect(() => {
    if (playlistRefreshTrigger > 0 && open) {
      const refreshPlaylists = async () => {
        try {
          const playlistsResponse = await dataFetcher(endpoints.getMyPlaylist);
          if (playlistsResponse.success && playlistsResponse.data) {
            setPlaylists(playlistsResponse.data);
            // Auto-select the newly created playlist by finding it in fetched data
            if (newlyCreatedPlaylistId) {
              const newPlaylist = playlistsResponse.data.find(
                (p) => String(p.id) === String(newlyCreatedPlaylistId)
              );
              if (newPlaylist && !selectedPlaylists.includes(newPlaylist.id)) {
                setSelectedPlaylists((prev) => [...prev, newPlaylist.id]);
              }
              setNewlyCreatedPlaylistId(null);
            }
          }
        } catch (error) {
          console.error('Error refreshing playlists:', error);
        }
      };
      refreshPlaylists();
    }
  }, [playlistRefreshTrigger, open]);

  // Refresh subcategories when a new subcategory is created from the global modal
  useEffect(() => {
    if (subCategoryRefreshTrigger > 0 && open) {
      const refreshSubCategories = async () => {
        try {
          const subCategoriesResponse = await dataFetcher(endpoints.getMySubCategory);
          if (subCategoriesResponse.success && subCategoriesResponse.data) {
            setSubCategories(subCategoriesResponse.data);
            // Auto-select the newly created subcategory
            if (newlyCreatedSubCategoryId) {
              setSelectedSubCategory(newlyCreatedSubCategoryId);
              setNewlyCreatedSubCategoryId(null);
            }
          }
        } catch (error) {
          console.error('Error refreshing subcategories:', error);
        }
      };
      refreshSubCategories();
    }
  }, [subCategoryRefreshTrigger, open]);

  // Handle category creation callback - refresh list and select new category
  const handleCategoryCreated = async () => {
    try {
      const categoriesResponse = await dataFetcher(endpoints.getMyCategories);
      if (categoriesResponse.success && categoriesResponse.data) {
        // Sort categories to always show "All Videos" at the top
        const sortedCategoriesForDisplay = [...categoriesResponse.data].sort((a, b) => {
          const aIsAllVideos = a.title?.toLowerCase() === 'all videos';
          const bIsAllVideos = b.title?.toLowerCase() === 'all videos';
          if (aIsAllVideos && !bIsAllVideos) return -1;
          if (!aIsAllVideos && bIsAllVideos) return 1;
          return 0; // Keep original order for other categories
        });
        setCategories(sortedCategoriesForDisplay);
        // Select the most recently created category (last in the list by id)
        const sortedCategories = [...categoriesResponse.data].sort((a, b) => b.id - a.id);
        if (sortedCategories.length > 0) {
          const newestCategory = sortedCategories[0];
          setSelectedCategory({
            value: newestCategory.id,
            name: newestCategory.title,
          });
        }
      }
    } catch (error) {
      console.error('Error refreshing categories:', error);
    } finally {
      setShowCreateCategoryModal(false);
    }
  };

  const handleCustomThumbnailUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setAppSnackbar({
        open: true,
        message: 'Please upload a valid image file (JPG, PNG, or WebP)',
        severity: 'error',
      });
      return;
    }

    // Create preview URL
    const reader = new FileReader();
    reader.onload = (e) => {
      setCustomThumbnail(e.target.result);
      setIsCustomThumbnailSelected(true);
      setSelectedThumbnail(null);
      setIsVideoPlaying(false);
    };
    reader.readAsDataURL(file);
  };

  // Capture up to `count` evenly-spaced frames from a video Blob via a same-origin
  // object URL (no canvas taint). Works for codecs the browser can decode; returns
  // [] otherwise so the caller can fall back to ffmpeg.wasm.
  const captureFramesFromBlob = (blob, count = 6) => new Promise((resolve) => {
    const url = URL.createObjectURL(blob);
    const video = document.createElement('video');
    video.src = url;
    video.muted = true;
    video.playsInline = true;
    const done = (out) => { try { URL.revokeObjectURL(url); } catch (e) { /* ignore */ } video.remove(); resolve(out); };
    video.onerror = () => done([]);
    video.onloadeddata = () => {
      const { duration } = video;
      if (!Number.isFinite(duration) || duration <= 0 || !video.videoWidth) { done([]); return; }
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const interval = duration / (count + 1);
      const out = [];
      let i = 1;
      const next = () => {
        if (i > count) { done(out); return; }
        video.currentTime = interval * i;
        video.onseeked = () => {
          try {
            const scale = Math.min(1, 640 / (video.videoWidth || 640));
            canvas.width = Math.max(1, Math.round(video.videoWidth * scale));
            canvas.height = Math.max(1, Math.round(video.videoHeight * scale));
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            out.push(canvas.toDataURL('image/jpeg', 0.9));
          } catch (e) { /* skip frame */ }
          i += 1;
          next();
        };
      };
      next();
    };
  });

  // "Generate thumbnails" for an already-uploaded video (shown when it has fewer
  // than 3). Fetches the stored file, captures frames natively (fast), and falls
  // back to ffmpeg.wasm for codecs the browser can't decode. Generated frames are
  // base64 data URLs that persist through the existing custom-thumbnail save path.
  const handleGenerateThumbnails = async () => {
    setIsGeneratingThumbs(true);
    try {
      let frames = [];

      // Preferred fast path: if the video has an HLS transcode, sample a few small
      // segments instead of downloading the whole source file.
      const transcoded = videoData?.transcoded_url;
      if (transcoded && isHlsUrl(transcoded)) {
        frames = await generateThumbnailsFromHls(transcoded, { count: 1 });
      }

      // Fallback: download the source file and capture locally / via wasm.
      if (!frames.length) {
        const srcUrl = videoData?.video_raw_file || videoData?.mp4_url
          || videoData?.video_file || currentVideoUrl;
        if (!srcUrl) throw new Error('No video source to generate from');
        const resp = await fetch(srcUrl);
        if (!resp.ok) throw new Error(`Fetch failed (${resp.status})`);
        const blob = await resp.blob();
        frames = await captureFramesFromBlob(blob, 1);
        if (!frames.length) {
          frames = await generateThumbnailsWithWasm(blob, { count: 1 });
        }
      }

      if (frames.length) {
        const firstNewIndex = thumbnails.length;
        setThumbnails((prev) => [...prev, ...frames]);
        setSelectedThumbnail(firstNewIndex);
        setIsCustomThumbnailSelected(false);
        setIsVideoPlaying(false);
        setAppSnackbar({ open: true, message: 'Thumbnails generated — pick one and Save', severity: 'success' });
      } else {
        setAppSnackbar({ open: true, message: 'Could not generate thumbnails from this video', severity: 'error' });
      }
    } catch (e) {
      setAppSnackbar({ open: true, message: e?.message || 'Could not generate thumbnails', severity: 'error' });
    } finally {
      setIsGeneratingThumbs(false);
    }
  };

  const handleClose = () => {
    // Reset state
    setVideoData(null);
    setTitle('');
    setDescription('');
    setVisibility('2');
    setSelectedCategory({ value: '', name: '' });
    setSelectedSubCategory('');
    setSelectedPlaylists([]);
    setSelectedGroups([]);
    setThumbnails([]);
    setSelectedThumbnail(0);
    setCustomThumbnail(null);
    setIsCustomThumbnailSelected(false);
    setIsVideoPlaying(false);
    setShowAdditionalFields(true);
    setIsSaving(false);
    setIsLoading(false);
    setVideoDuration(0);
    setVideoSize(0);
    setIsDownloadDisabled(true);

    onClose();
  };

  const handleDownload = () => {
    if (!onDownloadVideos || !videoData) {
      setAppSnackbar({
        open: true,
        message: 'Video file not available',
        severity: 'error',
      });
      return;
    }

    // Route through the shared downloadSelectedVideos function
    onDownloadVideos([{
      content_id: videoData.content_id || videoId,
      video_id: videoData.video_id || videoId,
      title: videoData.title || 'video',
      extension: videoData.extension || 'mp4',
      video_file: videoData.video_file,
    }]);
  };

  const handleSave = async () => {
    if (!videoData) return;

    // Validate group selection if visibility is Group
    if (
      visibility === '3'
      && (!selectedGroups || selectedGroups.length === 0)
    ) {
      alert(
        'Please select at least one group when Group visibility is selected.'
      );
      return;
    }

    setIsSaving(true);

    try {
      const apiUrl = `${baseUrl}${endpoints.addVideoContent}`;
      const formData = new FormData();

      formData.append('video_id', videoId);
      formData.append('draft_id', videoData.draft_id || '');
      formData.append('title', title || '');
      formData.append('category_id', selectedCategory.value || '');

      // Handle description - use 'delete' if empty
      if (description && description.trim()) {
        formData.append('description', description);
      } else {
        formData.append('description', 'delete');
      }

      formData.append('visibility', visibility || '2');
      formData.append('status', 1);

      // Add subcategory if selected
      if (selectedSubCategory) {
        formData.append('subcategory_id', selectedSubCategory);
      }

      // Add playlists if selected (send as comma-separated like groups)
      if (selectedPlaylists && selectedPlaylists.length > 0) {
        formData.append('playlist_id', selectedPlaylists.join(','));
      }

      // Add group_id as comma-separated string if visibility is Group
      if (visibility === '3' && selectedGroups && selectedGroups.length > 0) {
        formData.append('group_id', selectedGroups.join(','));
      }

      // Handle thumbnail_list - send comma-separated URLs. Freshly-generated
      // frames are base64 data URLs (not hosted URLs), so exclude them here.
      const urlThumbnails = (thumbnails || []).filter(
        (t) => typeof t === 'string' && !t.startsWith('data:'),
      );
      if (urlThumbnails.length > 0) {
        formData.append('thumbnail_list', urlThumbnails.join(','));
      }

      // Handle thumbnail selection
      const selectedThumb = thumbnails[selectedThumbnail];
      if (isCustomThumbnailSelected && customThumbnail) {
        // For custom thumbnail, send as base64
        formData.append('custom_thumbnails', customThumbnail.split('base64,')[1]);
      } else if (selectedThumb && selectedThumb.startsWith('data:')) {
        // A freshly-generated frame — persist via the custom-thumbnail path.
        formData.append('custom_thumbnails', selectedThumb.split('base64,')[1]);
      } else if (selectedThumb) {
        // If user selected an existing thumbnail, send its URL
        formData.append('thumbnails', selectedThumb);
      }

      const response = await axios.post(apiUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response?.data?.success) {
        setAppSnackbar({
          open: true,
          message: 'Video updated successfully',
          severity: 'success',
        });

        if (onSaveSuccess) {
          onSaveSuccess(response.data);
        }

        handleClose();
      } else {
        setAppSnackbar({
          open: true,
          message: response?.data?.message || 'Failed to update video',
          severity: 'error',
        });
      }
    } catch (error) {
      console.error('Error updating video:', error);
      setAppSnackbar({
        open: true,
        message: 'Error updating video',
        severity: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 MB';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${Math.round((bytes / 1024 ** i) * 100) / 100} ${sizes[i]}`;
  };

  const handleAdditionalOptionsToggle = () => {
    const newState = !showAdditionalFields;
    setShowAdditionalFields(newState);

    // If expanding, scroll to the additional options section after a brief delay
    if (newState && additionalOptionsRef.current) {
      setTimeout(() => {
        additionalOptionsRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 150);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        className: 'vem-root',
        style: {
          borderRadius: 18,
          maxWidth: 1100,
          maxHeight: '92vh',
          boxShadow: '0 24px 60px rgba(15,17,28,0.18)',
        },
      }}
    >
      <style>{VEM_STYLES}</style>
      <DialogContent
        style={{ padding: 0, display: 'flex', flexDirection: 'column', height: '100%' }}
      >
        {/* Header */}
        <div
          style={{
            padding: '18px 24px',
            backgroundColor: '#fff',
            borderBottom: `1px solid ${V2.border}`,
            borderRadius: '18px 18px 0 0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div className="vem-chip" style={{ display: 'flex', alignItems: 'center' }}>
            <div
              style={{
                background: 'linear-gradient(135deg, #2a2f3a 0%, #15171c 100%)',
                borderRadius: '12px',
                padding: '10px',
                display: 'flex',
                marginRight: 12,
                color: '#fff',
                boxShadow: '0 4px 12px rgba(15,17,28,0.28)',
              }}
            >
              <IconPencil className="vem-pencil" size={20} />
            </div>
            <h2
              style={{
                margin: 0,
                color: V2.ink,
                fontSize: 18,
                fontWeight: 700,
                fontFamily: 'Montserrat',
                letterSpacing: '-0.01em',
              }}
            >
              Edit Video Details
            </h2>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {!isLoading && (
              <Button
                onClick={handleDownload}
                variant="contained"
                disableRipple
                className="vem-iconbtn"
                disabled={isDownloadDisabled || isAlreadyDownloading}
                title={isAlreadyDownloading ? 'Download in progress...' : isDownloadDisabled ? 'Download not available' : 'Download video'}
                style={{
                  backgroundColor: (isDownloadDisabled || isAlreadyDownloading) ? '#e5e7eb' : V2.ink,
                  color: (isDownloadDisabled || isAlreadyDownloading) ? '#9ca3af' : 'white',
                  borderRadius: 12,
                  padding: '8px 14px',
                  fontSize: 12.5,
                  fontWeight: 600,
                  fontFamily: 'Montserrat',
                  boxShadow: 'none',
                  textTransform: 'none',
                  minWidth: 'auto',
                  gap: 6,
                  cursor: (isDownloadDisabled || isAlreadyDownloading) ? 'not-allowed' : 'pointer',
                }}
              >
                <IconDownload size={16} arrowClassName="vem-dl-arrow" />
                {isAlreadyDownloading ? 'Downloading...' : 'Download'}
              </Button>
            )}
            <IconButton
              onClick={handleClose}
              disableRipple
              className="vem-iconbtn"
              style={{ color: V2.sub, backgroundColor: V2.iconBox, borderRadius: 12, padding: 8 }}
              size="large"
            >
              <IconX size={18} className="vem-x" />
            </IconButton>
          </div>
        </div>

        {/* Content */}
        <div style={{ overflowY: 'auto', padding: '0 24px' }}>
          {isLoading ? (
            // Beautiful loader while fetching video details
            (
<div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                height: '100%',
                minHeight: 450,
                padding: '40px 20px',
              }}
>
              <div
                style={{
                  position: 'relative',
                  marginBottom: 32,
                }}
              >
                <CircularProgress
                  size={80}
                  thickness={3}
                  style={{ color: V2.ink }}
                />
                <span
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    color: V2.ink,
                    display: 'flex',
                  }}
                >
                  <IconFilm size={30} />
                </span>
              </div>
              <h3
                style={{
                  color: '#1A1A1A',
                  fontSize: 18,
                  fontWeight: 600,
                  margin: '0 0 8px 0',
                  fontFamily: 'Montserrat',
                  textAlign: 'center',
                }}
              >
                Loading video details...
              </h3>
              <p
                style={{
                  color: '#6B7280',
                  fontSize: 14,
                  margin: 0,
                  fontFamily: 'Montserrat',
                  textAlign: 'center',
                  maxWidth: 300,
                }}
              >
                Please wait while we fetch your video information
              </p>
</div>
)
          ) : videoData ? (
            <div style={{ display: 'flex', height: 'calc(100% - 140px)', gap: 0 }}>
              {/* Left Side: Form Controls - 45% */}
              <div
                style={{
                  flex: '0 0 45%',
                  backgroundColor: V2.bg,
                  padding: '24px 22px',
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 16,
                  borderRight: `1px solid ${V2.border}`,
                }}
              >
                {/* Video Title */}
                <div style={cardBox}>
                  <label style={fieldLabel}>
                    Video Title
                  </label>
                  <TextField
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Enter an engaging title..."
                    variant="outlined"
                    fullWidth
                    size="small"
                    InputProps={{
                      style: {
                        backgroundColor: '#fff',
                        borderRadius: 12,
                        fontFamily: 'Montserrat',
                        fontSize: 14,
                      },
                    }}
                  />
                </div>

                {/* Category & Visibility Row */}
                <div
                  style={{
                    ...cardBox,
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 14,
                  }}
                >
                  {/* Category */}
                  <div>
                    <label style={fieldLabel}>
                      Category
                    </label>
                    <FormControl fullWidth variant="outlined" size="small">
                      <Select
                        value={selectedCategory.value}
                        onChange={(e) => {
                          if (e.target.value === '__create_new__') {
                            setShowCreateCategoryModal(true);
                            return;
                          }
                          const selectedCat = categories?.find(
                            (cat) => cat?.id === e.target.value
                          );
                          setSelectedCategory({
                            value: e.target.value,
                            name: selectedCat?.title,
                          });
                        }}
                        style={{
                          backgroundColor: '#fff',
                          borderRadius: 12,
                          fontFamily: 'Montserrat',
                          fontSize: 13,
                        }}
                        MenuProps={{
                          PaperProps: {
                            className: 'vem-menu',
                            style: {
                              maxHeight: 300,
                            },
                          },
                          anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left',
                          },
                          transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left',
                          },
                          getContentAnchorEl: null,
                          autoFocus: false,
                          disableAutoFocusItem: true,
                        }}
                      >
                        <MenuItem value="__create_new__">
                          <div style={{ display: 'flex', alignItems: 'center', color: V2.slate }}>
                            <IconPlus size={16} style={{ marginRight: 8 }} />
                            <span style={{ fontSize: 13, fontFamily: 'Montserrat', fontWeight: 600 }}>Create New Category</span>
                          </div>
                        </MenuItem>
                        <Divider style={{ margin: '4px 0' }} />
                        {categories?.map((cat) => (
                          <MenuItem key={cat?.id} value={cat?.id}>
                            <div style={{ display: 'flex', alignItems: 'center', color: V2.slate }}>
                              <IconFolder size={16} style={{ marginRight: 8 }} />
                              <span style={{ fontSize: 13, fontFamily: 'Montserrat', color: V2.ink }}>{cat?.title}</span>
                            </div>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>

                  {/* Visibility */}
                  <div>
                    <label style={fieldLabel}>
                      Visibility
                    </label>
                    <FormControl fullWidth variant="outlined" size="small">
                      <Select
                        value={visibility}
                        onChange={(e) => setVisibility(e.target.value)}
                        style={{
                          backgroundColor: '#fff',
                          borderRadius: 12,
                          fontFamily: 'Montserrat',
                          fontSize: 13,
                        }}
                        MenuProps={{
                          PaperProps: { className: 'vem-menu' },
                          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                          transformOrigin: { vertical: 'top', horizontal: 'left' },
                          getContentAnchorEl: null,
                        }}
                      >
                        <MenuItem value="2">
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <IconGlobe size={16} style={{ marginRight: 8, color: '#10B981' }} />
                            <span style={{ fontSize: 13, fontFamily: 'Montserrat', color: V2.ink }}>All Viewers</span>
                          </div>
                        </MenuItem>
                        <MenuItem value="1">
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <IconLock size={16} style={{ marginRight: 8, color: '#EF4444' }} />
                            <span style={{ fontSize: 13, fontFamily: 'Montserrat', color: V2.ink }}>Private</span>
                          </div>
                        </MenuItem>
                        <MenuItem value="3">
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <IconUsers size={16} style={{ marginRight: 8, color: '#8B5CF6' }} />
                            <span style={{ fontSize: 13, fontFamily: 'Montserrat', color: V2.ink }}>Group</span>
                          </div>
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </div>
                </div>

                {/* Group Selection (when visibility is Group) */}
                {visibility === '3' && (
                  <div style={cardBox}>
                    <label style={fieldLabel}>
                      Select Groups
                    </label>
                    <div
                      style={{
                        backgroundColor: '#fff',
                        borderRadius: 12,
                        padding: '8px',
                        maxHeight: '160px',
                        overflowY: 'auto',
                        border:
                          visibility === '3' && (!selectedGroups || selectedGroups.length === 0)
                            ? '2px solid #EF4444'
                            : `1px solid ${V2.borderStrong}`,
                        boxShadow:
                          visibility === '3' && (!selectedGroups || selectedGroups.length === 0)
                            ? '0 0 0 3px rgba(239, 68, 68, 0.1)'
                            : 'none',
                      }}
                    >
                      {!groupsLoaded ? (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '60px',
                          }}
                        >
                          <CircularProgress
                            size={16}
                            style={{ color: V2.ink }}
                          />
                          <span
                            style={{
                              marginLeft: '8px',
                              color: '#6B7280',
                              fontSize: '14px',
                              fontFamily: 'Montserrat',
                            }}
                          >
                            Loading groups...
                          </span>
                        </div>
                      ) : groups.length === 0 ? (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '60px',
                            backgroundColor:
                              visibility === '3' ? '#FEF2F2' : 'transparent',
                            border:
                              visibility === '3' ? '2px solid #EF4444' : 'none',
                            borderRadius: visibility === '3' ? '8px' : '0',
                            boxShadow:
                              visibility === '3'
                                ? '0 0 0 3px rgba(239, 68, 68, 0.1)'
                                : 'none',
                          }}
                        >
                          <span
                            style={{
                              color: '#9CA3AF',
                              fontStyle: 'italic',
                              fontSize: '14px',
                              fontFamily: 'Montserrat',
                            }}
                          >
                            No groups available
                          </span>
                        </div>
                      ) : (
                        groups.map((group) => (
                          <FormControlLabel
                            key={group.id}
                            control={(
                              <Checkbox
                                checked={selectedGroups.includes(group.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedGroups([
                                      ...selectedGroups,
                                      group.id,
                                    ]);
                                  } else {
                                    setSelectedGroups(
                                      selectedGroups.filter(
                                        (id) => id !== group.id
                                      )
                                    );
                                  }
                                }}
                                style={{ color: '#8B5CF6' }}
                              />
                            )}
                            label={(
                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                <IconUsers size={15} style={{ marginRight: 8, color: '#8B5CF6' }} />
                                <span
                                  style={{
                                    fontFamily: 'Montserrat',
                                    fontSize: 13,
                                    color: V2.ink,
                                    fontWeight: 500,
                                  }}
                                >
                                  {group.title}
                                </span>
                              </div>
                            )}
                            style={{
                              margin: '0',
                              padding: '6px 8px',
                              width: '100%',
                              borderRadius: '8px',
                              transition: 'background-color 0.2s ease',
                            }}
                            onMouseOver={(e) => {
                              e.currentTarget.style.backgroundColor = '#f3f4f6';
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          />
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* Additional Options - Combined Box */}
                <div style={{ ...cardBox, padding: 18 }}>
                  {/* Toggle Header */}
                  <div
                    ref={additionalOptionsRef}
                    onClick={handleAdditionalOptionsToggle}
                    className="vem-opt-toggle"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      padding: '4px 0',
                      marginBottom: showAdditionalFields ? 16 : 0,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div
                        style={{
                          backgroundColor: V2.iconBox,
                          borderRadius: '10px',
                          padding: '7px',
                          display: 'flex',
                          marginRight: 10,
                          color: V2.slate,
                        }}
                      >
                        <IconSliders size={16} className="vem-sliders" />
                      </div>
                      <span
                        style={{
                          color: V2.ink,
                          fontSize: 13.5,
                          fontWeight: 700,
                          fontFamily: 'Montserrat',
                        }}
                      >
                        Additional Options (Optional)
                      </span>
                    </div>
                    {showAdditionalFields ? (
                      <IconChevronUp size={20} className="vem-chev" style={{ color: V2.sub }} />
                    ) : (
                      <IconChevronDown size={20} className="vem-chev" style={{ color: V2.sub }} />
                    )}
                  </div>

                  {/* Additional Fields */}
                  <Collapse in={showAdditionalFields}>
                    <div>
                    {/* Description */}
                    <div style={{ marginBottom: 16 }}>
                      <label style={fieldLabel}>
                        Description
                      </label>
                      <TextField
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Add a compelling description for your video..."
                        variant="outlined"
                        fullWidth
                        multiline
                        minRows={3}
                        size="small"
                        InputProps={{
                          style: {
                            backgroundColor: '#fff',
                            borderRadius: 12,
                            fontFamily: 'Montserrat',
                            fontSize: 13,
                          },
                        }}
                      />
                    </div>

                    {/* Subcategory and Playlist Grid */}
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 14,
                      }}
                    >
                      {/* Subcategory */}
                      <div>
                        <label style={fieldLabel}>
                          Subcategory
                        </label>
                        <FormControl fullWidth variant="outlined" size="small">
                          <Select
                            value={selectedSubCategory}
                            onChange={(e) => {
                              if (e.target.value === '__create_new__') {
                                setShowAddSubCategoryModal(true);
                                return;
                              }
                              setSelectedSubCategory(e.target.value);
                            }}
                            style={{
                              backgroundColor: '#fff',
                              borderRadius: 12,
                              fontFamily: 'Montserrat',
                              fontSize: 13,
                            }}
                            MenuProps={{
                              variant: 'menu',
                              PaperProps: {
                                className: 'vem-menu',
                                style: {
                                  maxHeight: 300,
                                },
                              },
                              anchorOrigin: {
                                vertical: 'bottom',
                                horizontal: 'left',
                              },
                              transformOrigin: {
                                vertical: 'top',
                                horizontal: 'left',
                              },
                              getContentAnchorEl: null,
                            }}
                          >
                            <MenuItem
                              value="__create_new__"
                              style={{
                                position: 'sticky',
                                top: 0,
                                backgroundColor: 'white',
                                zIndex: 1,
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', color: V2.slate }}>
                                <IconPlus size={16} style={{ marginRight: 8 }} />
                                <span style={{ fontSize: 13, fontFamily: 'Montserrat', fontWeight: 600 }}>Add New Subcategory</span>
                              </div>
                            </MenuItem>
                            <Divider style={{ margin: '4px 0', position: 'sticky', top: 36, backgroundColor: 'white', zIndex: 1 }} />
                            <MenuItem value="">
                              <span style={{ fontSize: 13, fontFamily: 'Montserrat', color: '#9ca3af' }}>None</span>
                            </MenuItem>
                            {subCategories.map((subcat) => (
                              <MenuItem key={subcat.id} value={subcat.id}>
                                <div style={{ display: 'flex', alignItems: 'center', color: V2.slate }}>
                                  <IconFolder size={15} style={{ marginRight: 8 }} />
                                  <span style={{ fontSize: 13, fontFamily: 'Montserrat', color: V2.ink }}>{subcat.title}</span>
                                </div>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </div>

                      {/* Playlist */}
                      <div>
                        <label style={fieldLabel}>
                          Playlists
                        </label>
                        <FormControl fullWidth variant="outlined" size="small">
                          <Select
                            multiple
                            value={selectedPlaylists}
                            onChange={(e) => {
                              const { value } = e.target;
                              // Check if "Create New" was clicked
                              if (value.includes('__create_new__')) {
                                setShowAddPlaylistModal(true);
                                return;
                              }
                              // Normalize to numbers and dedupe so a playlist can't be
                              // added twice via a string/number type mismatch.
                              const normalized = value
                                .map((id) => Number(id))
                                .filter((id) => !Number.isNaN(id));
                              setSelectedPlaylists([...new Set(normalized)]);
                            }}
                            style={{
                              backgroundColor: '#fff',
                              borderRadius: 12,
                              fontFamily: 'Montserrat',
                              fontSize: 13,
                            }}
                            renderValue={(selected) => {
                              if (selected.length === 0) {
                                return <em style={{ color: '#9CA3AF' }}>Select playlists...</em>;
                              }
                              return `${selected.length} playlist${selected.length !== 1 ? 's' : ''} selected`;
                            }}
                            displayEmpty
                            MenuProps={{
                              variant: 'menu',
                              PaperProps: {
                                className: 'vem-menu',
                                style: {
                                  maxHeight: 300,
                                },
                              },
                              anchorOrigin: {
                                vertical: 'bottom',
                                horizontal: 'left',
                              },
                              transformOrigin: {
                                vertical: 'top',
                                horizontal: 'left',
                              },
                              getContentAnchorEl: null,
                            }}
                          >
                            <MenuItem
                              value="__create_new__"
                              style={{
                                position: 'sticky',
                                top: 0,
                                backgroundColor: 'white',
                                zIndex: 1,
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', color: V2.slate }}>
                                <IconPlus size={16} style={{ marginRight: 8 }} />
                                <span style={{ fontSize: 13, fontFamily: 'Montserrat', fontWeight: 600 }}>Add New Playlist</span>
                              </div>
                            </MenuItem>
                            <Divider style={{ margin: '4px 0', position: 'sticky', top: 36, backgroundColor: 'white', zIndex: 1 }} />
                            {playlists.length === 0 ? (
                              <MenuItem disabled>
                                <em style={{ color: '#9CA3AF' }}>No playlists available</em>
                              </MenuItem>
                            ) : (
                              playlists.map((playlist) => (
                                <MenuItem key={playlist.id} value={Number(playlist.id)}>
                                  <Checkbox
                                    checked={selectedPlaylists.includes(Number(playlist.id))}
                                    style={{ color: V2.ink }}
                                  />
                                  <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <IconListMusic size={15} style={{ marginRight: 8, color: V2.slate }} />
                                    <span
                                      style={{
                                        fontFamily: 'Montserrat',
                                        fontSize: 13,
                                        color: V2.ink,
                                        fontWeight: 500,
                                      }}
                                    >
                                      {playlist.title}
                                    </span>
                                  </div>
                                </MenuItem>
                              ))
                            )}
                          </Select>
                        </FormControl>
                      </div>
                    </div>
                    </div>
                  </Collapse>
                </div>

                {/* Save Button - Fixed at bottom of left panel */}
                <div style={{ marginTop: 'auto', paddingTop: 20 }}>
                  <Button
                    onClick={handleSave}
                    disabled={
                      !title.trim()
                      || !selectedCategory.value
                      || isSaving
                      || (visibility === '3' && (!selectedGroups || selectedGroups.length === 0))
                    }
                    fullWidth
                    variant="contained"
                    disableRipple
                    className="vem-iconbtn"
                    style={{
                      backgroundColor:
                        title.trim() && selectedCategory.value && !isSaving
                        && !(visibility === '3' && (!selectedGroups || selectedGroups.length === 0))
                          ? V2.ink
                          : '#e5e7eb',
                      color:
                        title.trim() && selectedCategory.value && !isSaving
                        && !(visibility === '3' && (!selectedGroups || selectedGroups.length === 0))
                          ? 'white'
                          : '#9ca3af',
                      borderRadius: 12,
                      padding: '13px',
                      fontSize: 14,
                      fontWeight: 600,
                      fontFamily: 'Montserrat',
                      boxShadow: 'none',
                      textTransform: 'none',
                    }}
                  >
                    {isSaving ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
                        <CircularProgress size={18} style={{ color: 'white' }} />
                        <span>Saving...</span>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9 }}>
                        <IconCheck size={18} className="vem-save-check" />
                        <span>Save Changes</span>
                      </div>
                    )}
                  </Button>
                </div>
              </div>

              {/* Right Side: Thumbnail Preview - 55% */}
              <div
                style={{
                  flex: '0 0 55%',
                  backgroundColor: V2.bg,
                  padding: '24px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  position: 'relative',
                }}
              >
                {/* Main Video Preview */}
                <div
                  style={{
                    width: '100%',
                    maxWidth: '520px',
                    aspectRatio: '16/9',
                    backgroundColor: '#000',
                    borderRadius: 12,
                    overflow: 'hidden',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    border: `1px solid ${V2.border}`,
                  }}
                >
                  {isVideoPlaying && currentVideoUrl && !videoFailed ? (
                    <ReactPlayer
                      key={currentVideoUrl}
                      url={currentVideoUrl}
                      controls
                      playing
                      muted
                      width="100%"
                      height="100%"
                      style={{ position: 'absolute', top: 0, left: 0 }}
                      onReady={handleVideoReady}
                      onError={handleVideoError}
                      config={{
                        file: {
                          forceVideo: true,
                          attributes: {
                            controlsList: 'nodownload',
                          },
                        },
                      }}
                    />
                  ) : videoFailed ? (
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f5f5f5',
                      }}
                    >
                      <span style={{ color: '#888', fontSize: 14 }}>No preview available</span>
                    </div>
                  ) : (isCustomThumbnailSelected && customThumbnail) || thumbnails.length > 0 ? (
                    <>
                      <img
                        src={isCustomThumbnailSelected && customThumbnail ? customThumbnail : thumbnails[selectedThumbnail]}
                        alt="Selected thumbnail"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                        }}
                      />
                      {currentVideoUrl && (
                        <button
                          type="button"
                          aria-label="Play video"
                          onClick={() => setIsVideoPlaying(true)}
                          className="vem-iconbtn"
                          style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: 64,
                            height: 64,
                            borderRadius: '50%',
                            backgroundColor: 'rgba(15, 17, 28, 0.55)',
                            backdropFilter: 'blur(2px)',
                            border: '1px solid rgba(255,255,255,0.25)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 0,
                            color: '#fff',
                          }}
                        >
                          <IconPlay size={26} className="vem-play" style={{ marginLeft: 3 }} />
                        </button>
                      )}
                    </>
                  ) : null}
                </div>

                {/* Video Info Badges */}
                {/* {videoData && (
                  <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '8px 16px',
                        backgroundColor: '#fff',
                        borderRadius: 8,
                        border: `1px solid ${V2.border}`,
                      }}
                    >
                        <span style={{ fontSize: 16 }}>⏱️</span>
                        {console.log('videoSize', videoSize, videoDuration)}
                      <span
                        style={{
                          fontWeight: 600,
                          fontSize: 14,
                          color: '#374151',
                          fontFamily: 'Montserrat',
                        }}
                      >
                        {videoDuration}
                      </span>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '8px 16px',
                        backgroundColor: '#fff',
                        borderRadius: 8,
                        border: `1px solid ${V2.border}`,
                      }}
                    >
                      <span style={{ fontSize: 16 }}>📁</span>
                      <span
                        style={{
                          fontWeight: 600,
                          fontSize: 14,
                          color: '#374151',
                          fontFamily: 'Montserrat',
                        }}
                      >
                        {videoSize}
                      </span>
                    </div>
                  </div>
                )} */}

                {/* Generate thumbnails — only for already-uploaded videos that
                    have an HLS transcode (so we can sample a small segment) AND
                    currently have fewer than 3 thumbnails. */}
                {thumbnails.length < 3 && isHlsUrl(videoData?.transcoded_url) && (
                  <div style={{ marginTop: 20, width: '100%', maxWidth: '520px' }}>
                    <button
                      type="button"
                      onClick={handleGenerateThumbnails}
                      disabled={isGeneratingThumbs}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '10px 18px',
                        borderRadius: 10,
                        border: `1px solid ${V2.border}`,
                        background: V2.ink,
                        color: '#fff',
                        fontFamily: 'Montserrat',
                        fontWeight: 700,
                        fontSize: 13,
                        cursor: isGeneratingThumbs ? 'default' : 'pointer',
                        opacity: isGeneratingThumbs ? 0.6 : 1,
                      }}
                    >
                      <IconImage size={15} />
                      {isGeneratingThumbs ? 'Generating…' : 'Generate thumbnails'}
                    </button>
                    <p style={{ margin: '8px 0 0', fontSize: 12, color: V2.sub, fontFamily: 'Montserrat' }}>
                      This video has few thumbnails — generate options from the video, pick one, and Save.
                    </p>
                  </div>
                )}

                {/* Thumbnail Selection Grid */}
                {thumbnails.length > 0 && (
                  <div style={{ marginTop: 24, width: '100%', maxWidth: '520px' }}>
                    <h3
                      style={{
                        color: V2.sub,
                        fontSize: 12,
                        fontWeight: 700,
                        fontFamily: 'Montserrat',
                        margin: '0 0 12px 0',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 7,
                      }}
                    >
                      <IconImage size={15} style={{ color: V2.slate }} />
                      Choose Thumbnail
                    </h3>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(4, 1fr)',
                        gap: 8,
                      }}
                    >
                      {/* Custom Thumbnail Upload Option */}
                      <div
                        className="vem-upload-tile"
                        onClick={() => {
                          if (customThumbnail) {
                            setIsCustomThumbnailSelected(true);
                            setSelectedThumbnail(null);
                            setIsVideoPlaying(false);
                          } else {
                            customThumbnailInputRef.current?.click();
                          }
                        }}
                        style={{
                          position: 'relative',
                          cursor: 'pointer',
                          borderRadius: 12,
                          overflow: 'hidden',
                          border: isCustomThumbnailSelected
                            ? `2px solid ${V2.ink}`
                            : `2px dashed ${V2.borderStrong}`,
                          aspectRatio: '16/9',
                          transition: 'all 0.2s ease',
                          boxShadow: isCustomThumbnailSelected
                            ? '0 0 0 3px rgba(21, 23, 28, 0.1)'
                            : 'none',
                          backgroundColor: customThumbnail ? 'transparent' : '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {customThumbnail ? (
                          <>
                            <img
                              src={customThumbnail}
                              alt="Custom thumbnail"
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                            />
                            {isCustomThumbnailSelected && (
                              <div
                                style={{
                                  position: 'absolute',
                                  top: 4,
                                  right: 4,
                                  width: 20,
                                  height: 20,
                                  backgroundColor: V2.ink,
                                  borderRadius: '50%',
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  color: 'white',
                                }}
                              >
                                <IconCheck size={12} />
                              </div>
                            )}
                          </>
                        ) : (
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: 6,
                              padding: 6,
                              color: V2.slate,
                            }}
                          >
                            <div
                              style={{
                                width: 30,
                                height: 30,
                                borderRadius: '50%',
                                backgroundColor: V2.iconBox,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <IconUpload size={16} className="vem-upload" />
                            </div>
                            <span
                              style={{
                                color: V2.sub,
                                fontSize: 9,
                                fontFamily: 'Montserrat',
                                fontWeight: 700,
                                textAlign: 'center',
                                textTransform: 'uppercase',
                                letterSpacing: '0.3px',
                              }}
                            >
                              Custom
                            </span>
                          </div>
                        )}
                      </div>
                      <input
                        ref={customThumbnailInputRef}
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        onChange={handleCustomThumbnailUpload}
                        style={{ display: 'none' }}
                      />
                      {/* Existing Thumbnails */}
                      {thumbnails?.map((thumb, index) => (
                        <div
                          key={index}
                          onClick={() => {
                            setSelectedThumbnail(index);
                            setIsCustomThumbnailSelected(false);
                            setIsVideoPlaying(false);
                          }}
                          style={{
                            position: 'relative',
                            cursor: 'pointer',
                            borderRadius: 12,
                            overflow: 'hidden',
                            border:
                              selectedThumbnail === index && !isCustomThumbnailSelected
                                ? `2px solid ${V2.ink}`
                                : `1px solid ${V2.border}`,
                            aspectRatio: '16/9',
                            transition: 'all 0.2s ease',
                            boxShadow:
                              selectedThumbnail === index && !isCustomThumbnailSelected
                                ? '0 0 0 3px rgba(21, 23, 28, 0.1)'
                                : 'none',
                          }}
                        >
                          <img
                            src={thumb}
                            alt={`Thumbnail ${index + 1}`}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                          {selectedThumbnail === index && !isCustomThumbnailSelected && (
                            <div
                              style={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                width: 20,
                                height: 20,
                                backgroundColor: V2.ink,
                                borderRadius: '50%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                color: 'white',
                              }}
                            >
                              <IconCheck size={12} />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </DialogContent>
      {/* Create Category Modal */}
      {showCreateCategoryModal && (
        <CreateCategoryPage
          initialOpen={showCreateCategoryModal}
          onModalClose={() => setShowCreateCategoryModal(false)}
          onCategoryCreated={handleCategoryCreated}
        />
      )}
    </Dialog>
  );
};

const VideoEditModal = (props) => (isNewUIEnabled()
  ? <VideoEditModalNew {...props} />
  : <VideoEditModalClassic {...props} />);

export default VideoEditModal;
